import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { getLibraryLessonComponents, type LibraryLessonDetailResponse } from '@/api/lcLearning'
import { Button3D } from '@/components/learner/ui/Button3D'
import { Card } from '@/components/learner/ui/Card'
import { curriculumAccentBgClass } from '@/components/learner/ui/courseTheme'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { Mascot } from '@/components/learner/ui/Mascot'
import { cn } from '@/lib/utils'
import { getLearnerErrorMessage } from '@/utils/errorMessage'
import { pickLocalizedText } from '@/utils/componentLabel'
import { getCourseAccent } from './model/libraryModel'
import { toReviewSteps, type ReviewStep } from './model/libraryReviewModel'

const STEP_TYPE_I18N: Record<string, string> = {
  video: 'library.stepType.video',
  quiz: 'library.stepType.quiz',
  toon: 'library.stepType.toon',
  guide: 'library.stepType.guide',
  tool: 'library.stepType.tool',
}

function useTypeLabel() {
  const { t } = useTranslation('learner')
  return (type: string): string => {
    const key = STEP_TYPE_I18N[type] ?? 'library.stepType.other'
    return t(key)
  }
}

export default function LibraryLessonReview() {
  const { t, i18n } = useTranslation(['learner', 'common'])
  const navigate = useNavigate()
  const { lessonId } = useParams<{ lessonId: string }>()
  const [detail, setDetail] = useState<LibraryLessonDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const typeLabel = useTypeLabel()

  const load = useCallback(async () => {
    if (!lessonId) {
      setError(t('learn.invalidPath'))
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const resp = await getLibraryLessonComponents(Number(lessonId))
      setDetail(resp)
    } catch (e) {
      console.error('getLibraryLessonComponents failed', e)
      setError(getLearnerErrorMessage(e, t))
    } finally {
      setLoading(false)
    }
  }, [lessonId, t])

  useEffect(() => {
    load()
  }, [load])

  const steps: ReviewStep[] = useMemo(
    () => (detail ? toReviewSteps(detail.components) : []),
    [detail],
  )

  const navigateToStep = (configComponentId: string, search = '') => {
    navigate(`/my/library/${lessonId}/steps/${configComponentId}${search}`)
  }

  if (loading) {
    return <LearnerLoading />
  }

  if (error || !detail) {
    return (
      <Frame>
        <Card
          size="md"
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center gap-4 py-8 text-center"
        >
          <AlertCircle className="size-10 text-lc-red-900" aria-label={t('aria.error')} />
          <p className="text-lc-body-2 text-lc-gray-700">{error ?? t('learn.lessonNotFound')}</p>
          <Button3D variant="lightblue" size="M" onClick={load}>
            {t('common:retry')}
          </Button3D>
        </Card>
      </Frame>
    )
  }

  const lessonHeading = detail.lessonSequence != null
    ? t('library.lessonNumberTitle', {
        order: detail.lessonSequence,
        title: detail.lessonTitle,
      })
    : detail.lessonTitle
  const chapterHeading = detail.moduleTitle
    ? detail.chapterNumber != null
      ? t('library.reviewChapterTitle', {
          chapter: detail.chapterNumber,
          title: detail.moduleTitle,
        })
      : detail.moduleTitle
    : null
  const courseTitle = detail.courseTitle ?? t('library.reviewTitle')
  const accentClass = curriculumAccentBgClass[getCourseAccent(courseTitle)]

  return (
    <Frame labelledBy="library-review-heading">
      <button
        type="button"
        onClick={() => navigate('/my/library')}
        className="inline-flex h-10 items-center gap-1.5 rounded-[10px] border border-lc-key-600 bg-lc-key-50 px-3 pr-4 text-lc-v3-body-2 font-semibold text-lc-key-600 transition-colors hover:bg-lc-key-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        {t('library.stepListBack')}
      </button>

      <article className="mt-4 flex flex-col gap-10 rounded-2xl bg-lc-bg-primary p-4 shadow-lc-v3-card">
        <header className="flex flex-col gap-6 px-2 pt-2 sm:flex-row sm:items-start sm:justify-between md:px-8 md:pt-6">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <span
              className={cn(
                'inline-flex rounded-full px-3 py-1.5 text-lc-v3-body-2 font-semibold leading-none tracking-[0.2px] text-white',
                accentClass,
              )}
            >
              <ContentMetadataText text={courseTitle} />
            </span>
            {chapterHeading ? (
              <p className="text-lc-v3-body-1 font-semibold text-lc-text-tertiary">
                <ContentMetadataText text={chapterHeading} />
              </p>
            ) : null}
            <h1
              id="library-review-heading"
              className="break-keep text-lc-v3-h3 font-bold text-lc-v3-gray-950"
            >
              <ContentMetadataText text={lessonHeading} />
            </h1>
          </div>
          <LessonReviewThumbnail
            url={detail.thumbnailUrl ?? null}
            accentClass={accentClass}
          />
        </header>

        <section
          aria-labelledby="library-review-steps-heading"
          className="flex flex-col gap-4 rounded-2xl bg-lc-v3-gray-100 px-4 pb-4 pt-5"
        >
          <h2
            id="library-review-steps-heading"
            className="px-0 text-lc-v3-body-1 font-semibold text-lc-v3-gray-800 md:px-4"
          >
            {t('library.stepListTitle')}
          </h2>

          {steps.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-white py-10 text-center shadow-lc-v3-card">
              <Mascot name="queda" shape="circle" size={72} decorative />
              <p className="text-lc-body-2 text-lc-gray-700">{t('library.stepListEmpty')}</p>
            </div>
          ) : (
            <ol className="flex flex-col gap-4">
              {steps.map((step, idx) => {
                const type = step.type.toLowerCase()
                const isVideo = type === 'video'
                const isQuiz = type === 'quiz'
                const wrongCount = isQuiz ? (step.component.wrongCount ?? 0) : 0
                const hasWrong = wrongCount > 0
                const blockQuizComponentId = step.blockQuizComponentId
                const title = pickLocalizedText(
                  step.component.label,
                  step.component.labelEn,
                  i18n.language,
                ) ?? typeLabel(step.type)

                return (
                  <ReviewStepCard key={step.id} number={idx + 1} title={title}>
                    {blockQuizComponentId ? (
                      <ReviewActionButton
                        variant="dark"
                        onClick={() => navigateToStep(
                          blockQuizComponentId,
                          '?video=with_block_quiz',
                        )}
                      >
                        {t('library.videoWithBlockQuiz')}
                      </ReviewActionButton>
                    ) : null}
                    {hasWrong ? (
                      <ReviewActionButton
                        variant="error"
                        aria-label={t('library.wrongOnlyWithCount', { count: wrongCount })}
                        onClick={() => navigateToStep(step.id, '?mode=wrong_only')}
                      >
                        {t('library.wrongOnlyAction')}
                        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-lc-error-400 px-1.5 py-1 text-[11px] font-bold leading-none text-white">
                          {wrongCount}
                        </span>
                      </ReviewActionButton>
                    ) : null}
                    <ReviewActionButton
                      onClick={() => navigateToStep(
                        step.id,
                        isVideo ? '?video=only' : '?mode=full',
                      )}
                    >
                      {isVideo ? t('library.videoOnly') : t('library.solveAll')}
                    </ReviewActionButton>
                  </ReviewStepCard>
                )
              })}
            </ol>
          )}
        </section>
      </article>
    </Frame>
  )
}

function LessonReviewThumbnail({
  url,
  accentClass,
}: {
  url: string | null
  accentClass: string
}) {
  const [failed, setFailed] = useState(false)

  return (
    <div
      className={cn(
        'relative aspect-video w-full shrink-0 overflow-hidden rounded-2xl border border-black/10 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.08)] sm:w-64',
        accentClass,
      )}
      aria-hidden="true"
    >
      {url && !failed ? (
        <img
          src={url}
          alt=""
          decoding="async"
          onError={() => setFailed(true)}
          className="absolute inset-0 size-full object-cover"
        />
      ) : null}
    </div>
  )
}

function ReviewStepCard({
  number,
  title,
  children,
}: {
  number: number
  title: string
  children: ReactNode
}) {
  return (
    <li className="flex min-h-[84px] flex-col gap-4 rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary px-4 py-3 shadow-lc-v3-card sm:flex-row sm:items-center sm:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-lc-key-500 text-lc-v3-body-1 font-bold text-white">
          {number}
        </span>
        <span className="min-w-0 flex-1 truncate text-lc-v3-body-1 font-bold text-lc-v3-gray-950">
          {title}
        </span>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end sm:gap-4">
        {children}
      </div>
    </li>
  )
}

type ReviewActionVariant = 'primary' | 'dark' | 'error'

function ReviewActionButton({
  variant = 'primary',
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ReviewActionVariant
}) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-10 min-w-[88px] items-center justify-center gap-2 rounded-full px-4 text-lc-v3-body-1 font-semibold text-white transition-[filter,transform] hover:brightness-95 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
        variant === 'primary' && 'bg-lc-key-500',
        variant === 'dark' && 'bg-lc-key-900',
        variant === 'error' && 'bg-lc-error-500',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

function Frame({ children, labelledBy }: { children: ReactNode; labelledBy?: string }) {
  return (
    <section
      aria-labelledby={labelledBy}
      className="mx-auto w-full max-w-[1088px] px-4 pb-10 pt-6 md:px-8 md:pt-8"
    >
      {children}
    </section>
  )
}
